import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { LOCAL_STORAGE_KEY_MAP } from '@/lib/constants/platform'
import { changeUserPasswordMutation } from '@/lib/queries/business/v2/user/password/update/mutations'

import { changePasswordFormSchema } from './constants'
import { type ChangePasswordFormSchema } from './types'

export const useChangePasswordPage = () => {
  const navigate = useNavigate()
  const form = useForm<ChangePasswordFormSchema>({
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
    resolver: zodResolver(changePasswordFormSchema),
  })

  const { mutateAsync: changeUserPassword, isPending: isUpdatingUserPassword } = useMutation(changeUserPasswordMutation)

  const handleClickGoBackButton = () => {
    void navigate({ to: '/settings' })
  }

  const handleSubmitForm = async (data: ChangePasswordFormSchema) => {
    await changeUserPassword(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: async () => {
          localStorage.removeItem(LOCAL_STORAGE_KEY_MAP.token)
          await navigate({
            to: '/sign-in',
          })
          toast.success('Password has been successfully changed!', {
            description: 'Please sign in again with the new password.',
            duration: 8000,
          })
        },
        onError: () => {
          toast.error('Failed to change password', {
            description: 'Please try again later.',
            duration: 8000,
          })
        },
      }
    )
  }

  const handleClickCancelButton = () => {
    void navigate({ to: '/settings' })
  }

  const formValues = form.getValues()
  const formErrors = form.formState.errors

  const isDisabledSaveButton = !formValues.currentPassword || !formValues.newPassword || !formValues.newPasswordConfirm
  const isDisabledCancelButton = isUpdatingUserPassword

  useEffect(
    function syncPasswordFields() {
      const subscription = form.watch(({ newPassword, newPasswordConfirm }, { name }) => {
        if (name === 'newPasswordConfirm' && newPassword) {
          void form.trigger('newPassword')
        }

        if (name === 'newPassword' && newPasswordConfirm) {
          void form.trigger('newPasswordConfirm')
        }
      })

      return () => {
        subscription.unsubscribe()
      }
    },
    [form]
  )

  return {
    form,
    formErrors,
    isUpdatingUserPassword,
    isDisabledSaveButton,
    isDisabledCancelButton,
    handleClickGoBackButton,
    handleSubmitForm,
    handleClickCancelButton,
  }
}
